// ============================================
// FAMUZA MARKET — Seller Dashboard
// With AI-powered image analysis for auto-fill
// ============================================

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { useApp } from '../context/AppContext';
import { Link, useNavigate } from 'react-router-dom';

const CATEGORIES = [
  'Shoes',
  'Salaula',
  'Returns — ASDA/George',
  'Returns — Amazon UK',
  'Returns — Lidl',
  'Furniture — Wayfair UK',
  'Electronics',
  'Baby Products',
  'Used Toys',
  'New Toys — Amazon',
  'Accessories & Handbags',
  'Pasta & Food',
  'Bulk Boxes',
];

const CONDITIONS = [
  'New — ASDA Returns',
  'New — Wayfair UK',
  'New — Amazon UK',
  'New — UK Stock',
  'Almost New — ASDA Returns',
  'Almost New — Amazon UK',
  'Almost New — Lidl Returns',
  'Used — Good Condition',
];

const emptyForm = {
  title: '',
  description: '',
  price: '',
  old_price: '',
  category: 'Shoes',
  condition: 'New — ASDA Returns',
  image_url: '',
  location: 'Lusaka',
  stock: 1,
};

export default function SellerDashboard() {
  const { user } = useApp();
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [aiSuggested, setAiSuggested] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [extraImages, setExtraImages] = useState([]); // additional images beyond main

  useEffect(() => {
    if (!user) navigate('/seller/auth');
  }, [user, navigate]);

  useEffect(() => {
    if (user) fetchProducts();
  }, [user]);

  async function fetchProducts() {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('seller_id', user.id)
      .order('created_at', { ascending: false });
    if (!error) setProducts(data || []);
    setLoading(false);
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/seller/auth?loggedout=true');
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }

    setUploading(true);
    setError('');
    setAiSuggested(false);

    const localPreview = URL.createObjectURL(file);
    setImagePreview(localPreview);
    setUploadedFile(file);

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}_${Date.now()}.${fileExt}`;
    const filePath = `seller-uploads/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      setError('Image upload failed: ' + uploadError.message);
      setImagePreview('');
      setUploading(false);
      return;
    }

    const { data } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath);

    setForm(prev => ({ ...prev, image_url: data.publicUrl }));
    setUploading(false);
  }

  async function handleAIAnalyse() {
    if (!uploadedFile) return;
    setAnalysing(true);
    setError('');

    try {
      // Convert image to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(uploadedFile);
      });

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
 	 'Content-Type': 'application/json',
  	 'x-api-key': process.env.REACT_APP_ANTHROPIC_API_KEY,
  	 'anthropic-version': '2023-06-01',
 	 'anthropic-dangerous-direct-browser-access': 'true'
	},
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [{
            role: 'user',
            content: [
              {
                type: 'image',
                source: {
                  type: 'base64',
                  media_type: uploadedFile.type,
                  data: base64
                }
              },
              {
                type: 'text',
                text: `You are helping a seller on FAMUZA Market, a Zambian online marketplace. 
Look at this product image and provide listing details.

Respond ONLY with a valid JSON object, no markdown, no extra text:
{
  "title": "short product title (max 60 chars)",
  "description": "2-3 sentence description mentioning key features, suitable for Zambian buyers",
  "category": "one of: Shoes, Salaula, Returns — ASDA/George, Returns — Amazon UK, Returns — Lidl, Furniture — Wayfair UK, Electronics, Baby Products, Used Toys, New Toys — Amazon, Accessories & Handbags, Pasta & Food, Bulk Boxes",
  "condition": "one of: New — ASDA Returns, New — Wayfair UK, New — Amazon UK, New — UK Stock, Almost New — ASDA Returns, Almost New — Amazon UK, Almost New — Lidl Returns, Used — Good Condition",
  "suggested_price": estimated price in Zambian Kwacha as a number only
}`
              }
            ]
          }]
        })
      });

      const data = await response.json();
      const text = data.content?.[0]?.text || '';

      // Clean and parse JSON
      const clean = text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);

      // Auto-fill the form
      setForm(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        description: parsed.description || prev.description,
        category: CATEGORIES.includes(parsed.category) ? parsed.category : prev.category,
        condition: CONDITIONS.includes(parsed.condition) ? parsed.condition : prev.condition,
        price: parsed.suggested_price || prev.price,
      }));

      setAiSuggested(true);
    } catch (err) {
      setError('AI analysis failed. Please fill in the details manually.');
    }

    setAnalysing(false);
  }

  function handleEdit(product) {
    setEditingProduct(product);
    setForm({
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      old_price: product.old_price || '',
      category: product.category || 'Shoes',
      condition: product.condition || 'New — ASDA Returns',
      image_url: product.image_url || '',
      location: product.location || 'Lusaka',
      stock: product.stock || 1,
    });
    setImagePreview(product.image_url || '');
    setShowForm(true);
    window.scrollTo(0, 0);
  }

  async function handleDelete(productId) {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId)
      .eq('seller_id', user.id);
    if (error) {
      setError('Failed to delete listing.');
    } else {
      setSuccess('Listing deleted.');
      fetchProducts();
      setTimeout(() => setSuccess(''), 3000);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.image_url) {
      setError('Please upload a photo for your listing.');
      return;
    }

    setSaving(true);

    const productData = {
      title: form.title,
      description: form.description,
      price: parseFloat(form.price),
      old_price: form.old_price ? parseFloat(form.old_price) : null,
      category: form.category,
      condition: form.condition,
      image_url: form.image_url,
      location: form.location,
      stock: parseInt(form.stock) || 1,
      seller_id: user.id,
      status: 'active',
    };

    if (editingProduct) {
      const { error } = await supabase
        .from('products')
        .update(productData)
        .eq('id', editingProduct.id)
        .eq('seller_id', user.id);
      if (error) {
        setError('Failed to update listing: ' + error.message);
      } else {
        // Save extra images
        if (extraImages.length > 0) {
          await supabase.from('product_images').delete().eq('product_id', editingProduct.id);
          await supabase.from('product_images').insert(
            extraImages.map((url, i) => ({ product_id: editingProduct.id, image_url: url, position: i + 1 }))
          );
        }
        setSuccess('Listing updated successfully!');
        setShowForm(false);
        setEditingProduct(null);
        setForm(emptyForm);
        setImagePreview('');
        setUploadedFile(null);
        setAiSuggested(false);
        setExtraImages([]);
        fetchProducts();
        setTimeout(() => setSuccess(''), 3000);
      }
    } else {
      const { data: inserted, error } = await supabase
        .from('products')
        .insert([productData])
        .select()
        .single();
      if (error) {
        setError('Failed to create listing: ' + error.message);
      } else {
        // Save extra images
        if (extraImages.length > 0 && inserted?.id) {
          await supabase.from('product_images').insert(
            extraImages.map((url, i) => ({ product_id: inserted.id, image_url: url, position: i + 1 }))
          );
        }
        setSuccess('Listing published successfully!');
        setShowForm(false);
        setForm(emptyForm);
        setImagePreview('');
        setUploadedFile(null);
        setAiSuggested(false);
        setExtraImages([]);
        fetchProducts();
        setTimeout(() => setSuccess(''), 3000);
      }
    }
    setSaving(false);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingProduct(null);
    setForm(emptyForm);
    setImagePreview('');
    setUploadedFile(null);
    setAiSuggested(false);
    setExtraImages([]);
    setError('');
  }

  const formatPrice = (p) => `K${Number(p).toLocaleString()}`;

  if (!user) return null;

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>

      {/* Top nav */}
      <div style={{ background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 800, color: '#F5F0E8' }}>
            FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
          </div>
        </Link>
       <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
  	 <Link to="/listings" style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', textDecoration: 'none' }}>
    		Browse Market
  	  </Link>
  		<div style={{ fontSize: '0.8rem', color: 'rgba(245,240,232,0.5)' }}>
    		{user?.email}
 	 </div>
  	<button
    	    onClick={handleSignOut}
            style={{ background: 'transparent', border: '0.5px solid rgba(255,255,255,0.15)', color: 'rgba(245,240,232,0.6)', borderRadius: 8, padding: '0.4rem 0.875rem', fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem' }}>

        {/* Welcome header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.6rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.25rem' }}>
            Seller <span style={{ color: '#E8622A' }}>Dashboard</span>
          </h1>
          <div style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.4)' }}>
            Welcome back, {user?.email}
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {[
            ['📦', 'Total Listings', products.length],
            ['✅', 'Active', products.filter(p => p.status === 'active').length],
            ['👀', 'Total Views', products.reduce((sum, p) => sum + (p.views || 0), 0)],
          ].map(([icon, label, value]) => (
            <div key={label} style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{icon}</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 800, color: '#F5F0E8' }}>{value}</div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(245,240,232,0.4)' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Shop link banner */}
        {user && (
          <div style={{ background: 'linear-gradient(135deg, #1A1208, #1E1E1E)', border: '0.5px solid rgba(232,98,42,0.25)', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.45)', marginBottom: '0.2rem' }}>🔗 Your shop link — share this everywhere</div>
              <div style={{ fontSize: '0.82rem', color: '#E8622A', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                famuza.net/shop/{user.id}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`https://famuza.net/shop/${user.id}`);
                  setCopiedLink(true);
                  setTimeout(() => setCopiedLink(false), 2000);
                }}
                style={{ padding: '0.5rem 1rem', background: copiedLink ? 'rgba(37,211,102,0.15)' : 'rgba(232,98,42,0.12)', color: copiedLink ? '#25D366' : '#E8622A', border: `0.5px solid ${copiedLink ? 'rgba(37,211,102,0.3)' : 'rgba(232,98,42,0.3)'}`, borderRadius: 100, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                {copiedLink ? '✓ Copied!' : '🔗 Copy'}
              </button>
              <button
                onClick={() => {
                  const msg = `Check out my shop on FAMUZA Market! 🛍️\n\nhttps://famuza.net/shop/${user.id}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                }}
                style={{ padding: '0.5rem 1rem', background: '#25D366', color: '#fff', border: 'none', borderRadius: 100, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                Share
              </button>
            </div>
          </div>
        )}

        {/* Success/Error messages */}
        {success && (
          <div style={{ background: 'rgba(37,211,102,0.1)', border: '0.5px solid rgba(37,211,102,0.3)', borderRadius: 10, padding: '0.875rem', fontSize: '0.85rem', color: '#25D366', marginBottom: '1rem' }}>
            ✅ {success}
          </div>
        )}
        {error && (
          <div style={{ background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 10, padding: '0.875rem', fontSize: '0.85rem', color: '#E8622A', marginBottom: '1rem' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Add listing button */}
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setEditingProduct(null); setForm(emptyForm); setImagePreview(''); setUploadedFile(null); setAiSuggested(false); setExtraImages([]); }}
            style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, padding: '0.75rem 1.5rem', fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer', marginBottom: '1.5rem' }}
          >
            + Add New Listing
          </button>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '1.25rem' }}>
              {editingProduct ? '✏️ Edit Listing' : '➕ Add New Listing'}
            </h2>
            <form onSubmit={handleSubmit}>

              {/* IMAGE UPLOAD */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.5rem', fontFamily: 'DM Sans, sans-serif' }}>
                  Product Photo *
                </label>

                <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageUpload} style={{ display: 'none' }} />

                {imagePreview ? (
                  <div>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '0.75rem' }}>
                      <img src={imagePreview} alt="Preview" style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 12, display: 'block' }} />
                      {uploading && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', color: '#fff' }}>
                          Uploading...
                        </div>
                      )}
                    </div>

                    {!uploading && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

                        {/* AI Analyse Button — the star of the show */}
                        {!aiSuggested && (
                          <button
                            type="button"
                            onClick={handleAIAnalyse}
                            disabled={analysing}
                            style={{ width: '100%', padding: '0.875rem', background: analysing ? '#333' : 'linear-gradient(135deg, #6B3FA0, #E8622A)', color: '#fff', border: 'none', borderRadius: 12, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: analysing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                          >
                            {analysing ? (
                              <>⏳ AI is analysing your photo...</>
                            ) : (
                              <>✨ Auto-fill with AI</>
                            )}
                          </button>
                        )}

                        {aiSuggested && (
                          <div style={{ background: 'rgba(107,63,160,0.15)', border: '0.5px solid rgba(107,63,160,0.4)', borderRadius: 10, padding: '0.75rem', fontSize: '0.82rem', color: 'rgba(245,240,232,0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ✨ AI has filled in the details — review and adjust if needed!
                            <button type="button" onClick={handleAIAnalyse} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'rgba(245,240,232,0.5)', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', flexShrink: 0 }}>
                              Re-analyse
                            </button>
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '0.6rem' }}>
                          <button type="button" onClick={() => galleryInputRef.current.click()}
                            style={{ flex: 1, padding: '0.6rem', background: 'rgba(74,144,217,0.12)', border: '0.5px solid rgba(74,144,217,0.3)', color: '#4A90D9', borderRadius: 10, fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer' }}>
                            🖼️ Change
                          </button>
                          <button type="button" onClick={() => cameraInputRef.current.click()}
                            style={{ flex: 1, padding: '0.6rem', background: 'rgba(37,211,102,0.12)', border: '0.5px solid rgba(37,211,102,0.3)', color: '#25D366', borderRadius: 10, fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer' }}>
                            📷 Retake
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ border: '2px dashed rgba(232,98,42,0.3)', borderRadius: 14, padding: '1.5rem 1rem', textAlign: 'center', marginBottom: '0.75rem', background: 'rgba(232,98,42,0.03)' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>📸</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#F5F0E8', marginBottom: '0.25rem' }}>Add a photo first</div>
                      <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.4)' }}>
                        Then AI will auto-fill your listing details ✨
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button type="button" onClick={() => galleryInputRef.current.click()}
                        style={{ flex: 1, padding: '0.875rem', background: 'rgba(74,144,217,0.12)', border: '0.5px solid rgba(74,144,217,0.3)', color: '#4A90D9', borderRadius: 12, fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer' }}>
                        🖼️ Choose from Gallery
                      </button>
                      <button type="button" onClick={() => cameraInputRef.current.click()}
                        style={{ flex: 1, padding: '0.875rem', background: 'rgba(37,211,102,0.12)', border: '0.5px solid rgba(37,211,102,0.3)', color: '#25D366', borderRadius: 12, fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer' }}>
                        📷 Take a Photo
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Extra images — only show after main image is uploaded */}
              {form.image_url && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.5rem', fontFamily: 'DM Sans, sans-serif' }}>
                    Additional Photos (optional — up to 4 more)
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {/* Existing extra images */}
                    {extraImages.map((url, i) => (
                      <div key={i} style={{ position: 'relative', width: 72, height: 72 }}>
                        <img src={url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10, border: '0.5px solid rgba(255,255,255,0.1)' }} />
                        <button
                          type="button"
                          onClick={() => setExtraImages(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#E8622A', border: 'none', color: '#fff', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                          ✕
                        </button>
                      </div>
                    ))}
                    {/* Add more button */}
                    {extraImages.length < 4 && (
                      <label style={{ width: 72, height: 72, borderRadius: 10, border: '1px dashed rgba(255,255,255,0.2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(245,240,232,0.4)', fontSize: '0.6rem', gap: 4 }}>
                        <span style={{ fontSize: '1.25rem' }}>+</span>
                        Add photo
                        <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files).slice(0, 4 - extraImages.length);
                            for (const file of files) {
                              if (file.size > 10 * 1024 * 1024) continue;
                              const fileExt = file.name.split('.').pop();
                              const fileName = `${user.id}_extra_${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
                              const filePath = `seller-uploads/${fileName}`;
                              const { error: uploadError } = await supabase.storage.from('product-images').upload(filePath, file, { upsert: true });
                              if (!uploadError) {
                                const { data } = supabase.storage.from('product-images').getPublicUrl(filePath);
                                setExtraImages(prev => [...prev, data.publicUrl]);
                              }
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.35)' }}>
                    More photos = more buyer confidence. Show different angles, labels, sizes.
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Product Title *</label>
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Tan Ankle Boots — Zip Side"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Price (K) *</label>
                  <input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required placeholder="e.g. 280"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Old Price (K) — optional</label>
                  <input type="number" value={form.old_price} onChange={e => setForm({ ...form, old_price: e.target.value })} placeholder="e.g. 380"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Category *</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Condition *</label>
                  <select value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }}>
                    {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Location</label>
                  <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Lusaka, Kitwe"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Stock quantity</label>
                  <input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} min="1"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Description</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe your item — size, colour, any defects..." rows={3}
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
              </div>

              {error && (
                <div style={{ background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 8, padding: '0.75rem', fontSize: '0.82rem', color: '#E8622A', marginBottom: '1rem' }}>
                  ⚠️ {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button type="submit" disabled={saving || uploading}
                  style={{ flex: 1, padding: '0.875rem', background: saving || uploading ? '#333' : '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: saving || uploading ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Publishing...' : uploading ? 'Uploading photo...' : editingProduct ? 'Update Listing' : 'Publish Listing'}
                </button>
                <button type="button" onClick={handleCancel}
                  style={{ padding: '0.875rem 1.5rem', background: 'transparent', color: 'rgba(245,240,232,0.6)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 12, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Products list */}
        <div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '1rem' }}>
            My Listings ({products.length})
          </h2>

          {loading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(245,240,232,0.4)', fontSize: '0.85rem' }}>
              Loading your listings...
            </div>
          )}

          {!loading && products.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(245,240,232,0.4)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📦</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '0.4rem' }}>No listings yet</div>
              <div style={{ fontSize: '0.82rem' }}>Tap "Add New Listing" to publish your first product!</div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {products.map(product => (
              <div key={product.id} style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', display: 'flex', gap: '1rem', padding: '0.875rem', alignItems: 'center' }}>
                <div style={{ width: 70, height: 70, borderRadius: 10, overflow: 'hidden', background: '#0D0D0D', flexShrink: 0 }}>
                  <img src={product.image_url} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#F5F0E8', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.title}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)', marginBottom: 4 }}>{product.category} · {product.condition}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.95rem', fontWeight: 800, color: '#8B6914' }}>{formatPrice(product.price)}</span>
                    {product.old_price && <span style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.3)', textDecoration: 'line-through' }}>{formatPrice(product.old_price)}</span>}
                    <span style={{ fontSize: '0.65rem', background: product.status === 'active' ? 'rgba(37,211,102,0.1)' : 'rgba(232,98,42,0.1)', color: product.status === 'active' ? '#25D366' : '#E8622A', border: `0.5px solid ${product.status === 'active' ? 'rgba(37,211,102,0.3)' : 'rgba(232,98,42,0.3)'}`, borderRadius: 100, padding: '1px 8px' }}>{product.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <button onClick={() => handleEdit(product)}
                    style={{ padding: '0.45rem 1rem', background: 'rgba(74,144,217,0.12)', border: '0.5px solid rgba(74,144,217,0.3)', color: '#4A90D9', borderRadius: 8, fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(product.id)}
                    style={{ padding: '0.45rem 1rem', background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.3)', color: '#E8622A', borderRadius: 8, fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
